import { ChangeEvent, useMemo, useState } from "react"
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap"
import { ImportedTravelOffer, parseTravelOfferTsv } from "./travel-offer-tsv"

const WP_SITE = "https://pristjek.ekstrabladet.dk"
const SUPPORTED_ARTICLE_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp"])

type TravelDoFetch = (path: string, body?: unknown, method?: string) => Promise<Response>

type TravelHeadlineResponse = {
	articleTitle?: string
	discoverTitle?: string
	source?: "ai"
	error?: string
}

export type TravelEditorialAngle = {
	id: string
	yoastTitle: string
	angle: string
	whyNow: string
	facts: string[]
	sources: Array<{
		title: string
		url: string
	}>
	researchedAt: string
	fallback: boolean
}

type TravelAnglesResponse = {
	items?: TravelEditorialAngle[]
	source?: "research" | "mixed" | "fallback"
	reason?: string
	notice?: string
	error?: string
}

type ImageDimensions = {
	width: number
	height: number
}

export type TravelCreatedArticle = {
	articleId: number
	previewLink: string
	editLink: string
	title: string
	topic: string
	articleTypeName: string
}

type TravelArticleDraftResponse = {
	articleId?: number
	previewLink?: string
	editLink?: string
	articleTypeName?: string
	topic?: string
	draft?: {
		title?: string
		topic?: string
	}
	createdPost?: {
		id?: number
	}
	error?: string
}

const PRICE_FIELDS = [
	"flypris_2_voksne_dkk",
	"hotelpris_min_dkk",
	"samlet_pris_dkk",
	"pris_pr_person_dkk",
]

const LINK_FIELDS = [
	"momondo_link",
	"trivago_link_dansk_dateret",
]

const HIDDEN_SECONDARY_FIELDS = new Set([
	"hotelpris_max_dkk",
	"hotelpris_anvendt_dkk",
	"flypriskilde",
	"bookinglink",
])

const PRIMARY_DETAIL_FIELDS = [
	"tilbudstype",
	"destination",
	"land",
	"region",
	"ferietype",
	"afrejsedato",
	"hjemrejsedato",
	"antal_naetter",
	"afrejselufthavn",
	"destinationslufthavn",
	"flyselskab",
	"flyrute",
	"antal_stop",
	"rejsetid",
	"bagage_status",
	"hotelnavn",
	"hotelomraade",
	"hoteladresse",
	"hotelstjerner",
	"hotelrating",
	"hotelrating_antal_anmeldelser",
	"vaerelsestype",
	"bookingudbyder",
	"ledighedsstatus",
	"kontrolniveau",
	"artikeloverskrift_forslag",
]

const FIELD_LABELS: Record<string, string> = {
	tilbudstype: "Tilbudstype",
	destination: "Destination",
	land: "Land",
	region: "Region",
	ferietype: "Ferietype",
	hvorfor_valgt: "Hvorfor tilbuddet er valgt",
	afrejsedato: "Afrejsedato",
	hjemrejsedato: "Hjemrejsedato",
	antal_naetter: "Antal nætter",
	afrejselufthavn: "Afrejselufthavn",
	destinationslufthavn: "Destinationslufthavn",
	flyselskab: "Flyselskab",
	flyrute: "Flyrute",
	antal_stop: "Antal stop",
	rejsetid: "Rejsetid",
	flypris_2_voksne_dkk: "Flypris for 2 voksne",
	flypriskilde: "Flypriskilde",
	bagage_status: "Bagagestatus",
	momondo_link: "Momondo-link",
	hotelnavn: "Hotelnavn",
	hotelomraade: "Hotelområde",
	hoteladresse: "Hoteladresse",
	hotelstjerner: "Hotelstjerner",
	hotelstjerner_kilde: "Kilde til hotelstjerner",
	hotelrating: "Hotelrating",
	hotelrating_kilde: "Kilde til hotelrating",
	hotelrating_antal_anmeldelser: "Antal hotelanmeldelser",
	hoteltype: "Hoteltype",
	hotelkaede: "Hotelkæde",
	checkin_checkout: "Check-in / check-out",
	morgenmad_status: "Morgenmad",
	betalings_og_afbestillingsvilkaar: "Betalings- og afbestillingsvilkår",
	vaerelsestype: "Værelsestype",
	hotelbeskrivelse: "Hotelbeskrivelse",
	hotelfaciliteter: "Hotelfaciliteter",
	hotelpris_min_dkk: "Laveste hotelpris",
	hotelpris_max_dkk: "Højeste hotelpris",
	hotelpris_anvendt_dkk: "Anvendt hotelpris",
	hotelpristype: "Hotelpristype",
	estimeringsmetode: "Estimeringsmetode",
	bookingudbyder: "Bookingudbyder",
	bookinglink: "Bookinglink",
	trivago_link_dansk_dateret: "Dateret Trivago-link",
	trivago_hotel_id: "Trivago hotel-ID",
	trivago_slug: "Trivago-slug",
	samlet_pris_dkk: "Samlet pris",
	pris_pr_person_dkk: "Pris pr. person",
	kontrolniveau: "Kontrolniveau",
	ledighedsstatus: "Ledighedsstatus",
	statusforklaring: "Statusforklaring",
	kontroltidspunkt: "Kontroltidspunkt",
	artikeloverskrift_forslag: "Forslag til artikeloverskrift",
	artikelintro: "Artikelintro",
	artikelvinkel: "Artikelvinkel",
	praktiske_forbehold: "Praktiske forbehold",
}

const priorityFieldNames = new Set([...PRICE_FIELDS, ...LINK_FIELDS, ...PRIMARY_DETAIL_FIELDS])

function getFieldLabel(field: string) {
	if (FIELD_LABELS[field]) return FIELD_LABELS[field]
	const label = field.replace(/_/g, " ")
	return label ? `${label.charAt(0).toUpperCase()}${label.slice(1)}` : field
}

function parsePrice(value?: string) {
	const normalized = String(value || "")
		.trim()
		.replace(/\s/g, "")
		.replace(/\.(?=\d{3}(?:\D|$))/g, "")
		.replace(",", ".")
	const price = Number(normalized)
	return Number.isFinite(price) ? price : null
}

function formatPrice(value?: string) {
	const price = parsePrice(value)
	if (price === null) return value || "-"
	return new Intl.NumberFormat("da-DK", {
		style: "currency",
		currency: "DKK",
		maximumFractionDigits: 0,
	}).format(price)
}

function formatTravelDate(value?: string) {
	if (!value) return "-"
	const parsed = new Date(`${value}T12:00:00`)
	if (Number.isNaN(parsed.getTime())) return value
	return parsed.toLocaleDateString("da-DK", {
		day: "numeric",
		month: "short",
		year: "numeric",
	})
}

function formatResearchTime(value?: string) {
	const date = new Date(String(value || ""))
	if (Number.isNaN(date.getTime())) return "Ukendt tidspunkt"
	return date.toLocaleString("da-DK", {
		day: "numeric",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	})
}

function extractHttpUrl(value?: string) {
	const match = String(value || "").match(/https?:\/\/[^\s—]+/i)
	if (!match) return ""
	try {
		const parsed = new URL(match[0])
		return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.toString() : ""
	} catch {
		return ""
	}
}

function readFileAsText(file: File) {
	return new Promise<string>((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => resolve(String(reader.result || ""))
		reader.onerror = () => reject(new Error("Filen kunne ikke læses."))
		reader.readAsText(file, "UTF-8")
	})
}

function readFileAsDataUrl(file: File) {
	return new Promise<string>((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => resolve(String(reader.result || ""))
		reader.onerror = () => reject(new Error("Billedfilen kunne ikke læses."))
		reader.readAsDataURL(file)
	})
}

function headlineContainsPrice(headline: string, value?: string) {
	const price = parsePrice(value)
	if (price === null) return false
	return extractHeadlinePrices(headline).some((headlinePrice) => Math.abs(headlinePrice - price) < 0.005)
}

function headlineMentionsPrice(headline: string) {
	return /\b(?:kr\.?|dkk)\b/i.test(String(headline || ""))
}

function extractHeadlinePrices(headline: string) {
	const prices: number[] = []
	const patterns = [
		/(\d[\d.\s]*(?:,\d{1,2})?)\s*(?:kr\.?|dkk)\b/gi,
		/\b(?:kr\.?|dkk)\s*(\d[\d.\s]*(?:,\d{1,2})?)/gi,
	]
	const text = String(headline || "")
	for (const pattern of patterns) {
		let match = pattern.exec(text)
		while (match) {
			const price = parsePrice(match[1])
			if (price !== null) prices.push(price)
			match = pattern.exec(text)
		}
	}
	return prices
}

function normalizeHeadlineComparison(value?: string) {
	return String(value || "")
		.toLocaleLowerCase("da-DK")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/\s+/g, " ")
		.trim()
}

function headlineIncludesValue(headline: string, value?: string) {
	const normalizedValue = normalizeHeadlineComparison(value)
	return Boolean(normalizedValue) && normalizeHeadlineComparison(headline).includes(normalizedValue)
}

export function isOfferDrivenTravelDiscoverTitle(headline: string) {
	const normalized = normalizeHeadlineComparison(headline)
	return headlineMentionsPrice(headline)
		|| /\b(billig|billigt|billige|pris|priser|prisniveau|rejsetilbud|tilbud|spar|rabat)\b/.test(normalized)
		|| /\bfra\s+\d{1,3}(?:[.\s]\d{3})+(?:,\d+)?\b/.test(normalized)
}

function isMetaTravelDiscoverTitle(headline: string) {
	return /\b(rejsevinkel|storbyvinkel|vinkel|rejseperiode|servicesituation|turistbudskab|efterarsprofil)\b/
		.test(normalizeHeadlineComparison(headline))
}

function isLowAppealTravelDiscoverTitle(headline: string) {
	const normalized = normalizeHeadlineComparison(headline)
	return /\b(tjek|natnet|natbus|lufthavnsbus|lufthavnsbussen|airport express|onlinebillet|onlinebilletter|driftsaendring|driftsaendringer|driftsforstyrrelse|driftsforstyrrelser|servicesituation|ankomstdag|ankomstdagen|teknisk|tekniske)\b/.test(normalized)
		|| /\b100e\b/.test(normalized)
		|| /\b\d+\s+dage\b/.test(normalized)
		|| /\bderfor er .+ ogsa\b/.test(normalized)
}

export function getTravelEditorialAngleIssues(angle: TravelEditorialAngle | null, destination: string, referenceDate = new Date()) {
	if (!angle) return ["Vælg en redaktionel Discover-vinkel."]
	const issues: string[] = []
	if (!angle.yoastTitle.trim()) issues.push("Yoast-titlen mangler.")
	if (angle.yoastTitle.trim().length > 80) issues.push("Yoast-titlen må højst være 80 tegn.")
	if (destination && !headlineIncludesValue(angle.yoastTitle, destination)) issues.push("Yoast-titlen skal indeholde destinationen.")
	if (isOfferDrivenTravelDiscoverTitle(angle.yoastTitle)) issues.push("Yoast-titlen skal være prisfri og redaktionel.")
	if (isMetaTravelDiscoverTitle(angle.yoastTitle)) issues.push("Yoast-titlen skal være skrevet til læseren og må ikke bruge interne ord som vinkel eller rejseperiode.")
	if (isLowAppealTravelDiscoverTitle(angle.yoastTitle)) issues.push("Yoast-titlen er for teknisk eller driftspræget og skal have en bredere menneskelig rejsevinkel.")
	if (angle.angle.trim().length < 5) issues.push("Den redaktionelle vinkel mangler.")
	if (angle.whyNow.trim().length < 20) issues.push("Forklaringen på, hvorfor vinklen er aktuel nu, er for kort.")
	if (!angle.facts.length) issues.push("Vinklen mangler verificerede fakta.")
	if (!angle.fallback && !angle.sources.length) issues.push("En researchet vinkel skal have mindst én kilde.")
	const researchedAt = new Date(angle.researchedAt)
	if (Number.isNaN(researchedAt.getTime())) {
		issues.push("Researchtidspunktet mangler eller er ugyldigt.")
	} else if (referenceDate.getTime() - researchedAt.getTime() > 24 * 60 * 60 * 1000) {
		issues.push("Vinklen er ældre end 24 timer og skal researches igen.")
	}
	return issues
}

export function getTravelHeadlineSetupIssues({
	articleTitle,
	discoverTitle,
	data,
	editorialAngle,
}: {
	articleTitle: string
	discoverTitle: string
	data: Record<string, string>
	editorialAngle?: TravelEditorialAngle | null
}) {
	const issues: string[] = []
	const destination = data.destination || ""
	if (!articleTitle.trim()) issues.push("Artikeloverskriften mangler.")
	if (articleTitle.trim().length > 110) issues.push("Artikeloverskriften må højst være 110 tegn.")
	if (destination && !headlineIncludesValue(articleTitle, destination)) issues.push("Artikeloverskriften skal indeholde destinationen.")
	if (!headlineContainsPrice(articleTitle, data.pris_pr_person_dkk)) issues.push("Artikeloverskriften skal indeholde den verificerede pris.")
	if (/\b(chokpris|vanvittig(?:t|e)?|sidste chance|skynd dig|kun i dag)\b/i.test(articleTitle)) {
		issues.push("Artikeloverskriften bruger udokumenteret eller vildledende salgssprog.")
	}
	if (!discoverTitle.trim()) issues.push("Discover-/SEO-titlen mangler.")
	if (discoverTitle.trim().length > 80) issues.push("Discover-/SEO-titlen må højst være 80 tegn.")
	if (destination && !headlineIncludesValue(discoverTitle, destination)) issues.push("Discover-/SEO-titlen skal indeholde destinationen.")
	if (headlineMentionsPrice(discoverTitle)) {
		issues.push("Discover-/SEO-titlen må ikke indeholde en pris.")
	}
	if (discoverTitle.trim() && isOfferDrivenTravelDiscoverTitle(discoverTitle)) {
		issues.push("Discover-/SEO-titlen skal have en redaktionel hvorfor-nu-vinkel og må ikke være tilbudsdrevet.")
	}
	if (discoverTitle.trim() && isMetaTravelDiscoverTitle(discoverTitle)) {
		issues.push("Discover-/SEO-titlen skal være skrevet til læseren og må ikke bruge interne ord som vinkel eller rejseperiode.")
	}
	if (discoverTitle.trim() && isLowAppealTravelDiscoverTitle(discoverTitle)) {
		issues.push("Discover-/SEO-titlen er for teknisk eller driftspræget og skal have en bredere menneskelig rejsevinkel.")
	}
	if (editorialAngle && normalizeHeadlineComparison(discoverTitle) !== normalizeHeadlineComparison(editorialAngle.yoastTitle)) {
		issues.push("Discover-/SEO-titlen skal matche den godkendte redaktionelle vinkel.")
	}
	return issues
}

export function getDiscoverImageBlockingIssues(dimensions: ImageDimensions | null) {
	if (!dimensions) return ["Billedets dimensioner er endnu ikke kontrolleret."]
	const issues: string[] = []
	if (dimensions.width < 1200) issues.push("Billedet skal være mindst 1.200 px bredt.")
	if (dimensions.width * dimensions.height <= 300000) issues.push("Billedet skal indeholde mere end 300.000 pixels.")
	return issues
}

function isGenericTravelImageAlt(value: string, data: Record<string, string>) {
	const destination = data.destination || ""
	const country = data.land || ""
	const genericValues = [
		destination,
		`${destination}${country ? ` i ${country}` : ""}`,
		`Rejse til ${destination}`,
		`Rejse til ${destination}${country ? ` i ${country}` : ""}`,
		`Billede af ${destination}`,
		`Billede fra ${destination}`,
		`Foto af ${destination}`,
		`Foto fra ${destination}`,
	].map(normalizeHeadlineComparison)
	return value.trim().length < 12 || genericValues.includes(normalizeHeadlineComparison(value))
}

function getTravelDateDifference(startValue?: string, endValue?: string) {
	const start = new Date(`${startValue || ""}T12:00:00Z`)
	const end = new Date(`${endValue || ""}T12:00:00Z`)
	if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
	return Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000))
}

export function getTravelOfferApprovalIssues(data: Record<string, string>) {
	const issues: string[] = []
	const prices = PRICE_FIELDS.map((field) => parsePrice(data[field]))
	if (prices.some((price) => price === null || price <= 0)) {
		issues.push("Alle fire priser skal være større end 0 kr.")
	}

	const [flightPrice, hotelPrice, totalPrice, perPersonPrice] = prices
	if (flightPrice !== null && hotelPrice !== null && totalPrice !== null) {
		const expectedTotal = Math.round(flightPrice + hotelPrice)
		if (Math.abs(totalPrice - expectedTotal) > 2) {
			issues.push("Samlet pris skal svare til flypris plus laveste hotelpris.")
		}
	}
	if (totalPrice !== null && perPersonPrice !== null) {
		const expectedPerPerson = Math.round(totalPrice / 2)
		if (Math.abs(perPersonPrice - expectedPerPerson) > 2) {
			issues.push("Pris pr. person skal svare til halvdelen af den samlede pris.")
		}
	}

	if (!data.destination?.trim()) issues.push("Destination mangler.")
	if (!data.hotelnavn?.trim()) issues.push("Hotelnavn mangler.")
	const dateDifference = getTravelDateDifference(data.afrejsedato, data.hjemrejsedato)
	if (dateDifference === null || dateDifference <= 0) {
		issues.push("Afrejse- og hjemrejsedato skal være gyldige.")
	} else if (Number(data.antal_naetter || 0) !== dateDifference) {
		issues.push("Antal nætter stemmer ikke med afrejse- og hjemrejsedato.")
	}
	if (!LINK_FIELDS.every((field) => Boolean(extractHttpUrl(data[field])))) {
		issues.push("Både Momondo- og Trivago-link skal være gyldige.")
	}
	return issues
}

export function buildLocalHeadlineSuggestions(data: Record<string, string>) {
	const destination = data.destination || "destinationen"
	const nights = Number(data.antal_naetter || 0)
	const duration = nights > 0 ? `${nights} nætter` : "Rejse"
	const price = formatPrice(data.pris_pr_person_dkk)

	return {
		articleTitle: `Rejsetilbud til ${destination}: ${duration.toLocaleLowerCase("da-DK")} fra ${price} pr. person`,
		discoverTitle: "",
	}
}

function getControlVariant(value?: string) {
	const normalized = String(value || "").toLocaleLowerCase("da-DK")
	if (normalized.includes("bekræftet") && !normalized.includes("ikke")) return "success"
	if (normalized.includes("estimat")) return "warning"
	return "secondary"
}

function OfferSummary({ offer }: { offer: ImportedTravelOffer }) {
	const data = offer.data
	return (
		<>
			<div className="d-flex justify-content-between align-items-start gap-3 mb-3">
				<div>
					<div className="text-muted small mb-1">{data.land || "Rejsetilbud"}</div>
					<h5 className="mb-1">{data.destination || "Ukendt destination"}</h5>
					<div className="small text-muted">
						{formatTravelDate(data.afrejsedato)} – {formatTravelDate(data.hjemrejsedato)}
						{data.antal_naetter ? ` · ${data.antal_naetter} nætter` : ""}
					</div>
				</div>
				{data.tilbudstype ? <Badge bg="dark">{data.tilbudstype}</Badge> : null}
			</div>

			<div className="p-3 rounded bg-light mb-3">
				<div className="small text-muted">Pris pr. person</div>
				<div className="fs-4 fw-bold">{formatPrice(data.pris_pr_person_dkk)}</div>
				<div className="small text-muted">Samlet for 2: {formatPrice(data.samlet_pris_dkk)}</div>
				<div className="border-top mt-3 pt-2 d-flex flex-column gap-1 small">
					<div className="d-flex justify-content-between gap-3">
						<span className="text-muted">Fly for 2 voksne</span>
						<span className="fw-semibold">{formatPrice(data.flypris_2_voksne_dkk)}</span>
					</div>
					<div className="d-flex justify-content-between gap-3">
						<span className="text-muted">Hotel</span>
						<span className="fw-semibold">{formatPrice(data.hotelpris_min_dkk)}</span>
					</div>
				</div>
			</div>

			<Row className="g-3 small">
				<Col sm={6}>
					<div className="text-muted">Fly</div>
					<div className="fw-semibold">{data.flyselskab || "-"}</div>
					<div>{data.flyrute || [data.afrejselufthavn, data.destinationslufthavn].filter(Boolean).join(" → ") || "-"}</div>
					<div className="text-muted">{data.antal_stop === "0" ? "Direkte" : data.antal_stop ? `${data.antal_stop} stop` : ""}</div>
				</Col>
				<Col sm={6}>
					<div className="text-muted">Hotel</div>
					<div className="fw-semibold">{data.hotelnavn || "-"}</div>
					<div>{[data.hotelstjerner ? `${data.hotelstjerner} stjerner` : "", data.hotelrating ? `Rating ${data.hotelrating}` : ""].filter(Boolean).join(" · ")}</div>
					<div className="text-muted">{data.hotelomraade || ""}</div>
				</Col>
			</Row>

			<div className="d-flex gap-2 flex-wrap mt-3">
				{data.kontrolniveau ? <Badge bg={getControlVariant(data.kontrolniveau)} text={getControlVariant(data.kontrolniveau) === "warning" ? "dark" : undefined}>{data.kontrolniveau}</Badge> : null}
				{data.ledighedsstatus ? <Badge bg="light" text="dark" className="border">{data.ledighedsstatus}</Badge> : null}
			</div>
		</>
	)
}

export default function TravelArticleWorkflow({
	doFetch,
	onArticleCreated,
}: {
	doFetch: TravelDoFetch
	onArticleCreated?: (article: TravelCreatedArticle) => void
}) {
	const [fileName, setFileName] = useState("")
	const [headers, setHeaders] = useState<string[]>([])
	const [offers, setOffers] = useState<ImportedTravelOffer[]>([])
	const [selectedOffer, setSelectedOffer] = useState<ImportedTravelOffer | null>(null)
	const [showOfferModal, setShowOfferModal] = useState(false)
	const [error, setError] = useState("")
	const [offerApproved, setOfferApproved] = useState(false)
	const [angleSuggestions, setAngleSuggestions] = useState<TravelEditorialAngle[]>([])
	const [selectedAngle, setSelectedAngle] = useState<TravelEditorialAngle | null>(null)
	const [angleApproved, setAngleApproved] = useState(false)
	const [isResearchingAngles, setIsResearchingAngles] = useState(false)
	const [angleSource, setAngleSource] = useState<"" | "research" | "mixed" | "fallback">("")
	const [angleNotice, setAngleNotice] = useState("")
	const [angleError, setAngleError] = useState("")
	const [imageDataUrl, setImageDataUrl] = useState("")
	const [imageFileName, setImageFileName] = useState("")
	const [imageDimensions, setImageDimensions] = useState<ImageDimensions | null>(null)
	const [imageAltText, setImageAltText] = useState("")
	const [articleTitle, setArticleTitle] = useState("")
	const [discoverTitle, setDiscoverTitle] = useState("")
	const [isGeneratingHeadlines, setIsGeneratingHeadlines] = useState(false)
	const [headlineSource, setHeadlineSource] = useState<"" | "ai" | "fallback">("")
	const [headlineError, setHeadlineError] = useState("")
	const [isCreatingArticle, setIsCreatingArticle] = useState(false)
	const [articleCreationError, setArticleCreationError] = useState("")
	const [createdArticle, setCreatedArticle] = useState<TravelCreatedArticle | null>(null)

	const additionalFields = useMemo(() => {
		return headers.filter((field) => !priorityFieldNames.has(field) && !HIDDEN_SECONDARY_FIELDS.has(field))
	}, [headers])

	const approvalIssues = useMemo(() => {
		if (!selectedOffer) return []
		return getTravelOfferApprovalIssues(selectedOffer.data)
	}, [selectedOffer])

	const canApproveOffer = Boolean(selectedOffer && !approvalIssues.length)
	const angleSelectionIssues = useMemo(() => getTravelEditorialAngleIssues(
		selectedAngle,
		selectedOffer?.data.destination || "",
	), [selectedAngle, selectedOffer])
	const canApproveAngle = Boolean(selectedAngle && !angleSelectionIssues.length)
	const discoverIncludesDestination = Boolean(selectedOffer?.data.destination
		&& headlineIncludesValue(discoverTitle, selectedOffer.data.destination))
	const articleIncludesDestination = Boolean(selectedOffer?.data.destination
		&& headlineIncludesValue(articleTitle, selectedOffer.data.destination))
	const articleIncludesPrice = Boolean(selectedOffer && headlineContainsPrice(articleTitle, selectedOffer.data.pris_pr_person_dkk))
	const headlineSetupIssues = useMemo(() => {
		if (!selectedOffer) return []
		return getTravelHeadlineSetupIssues({
			articleTitle,
			discoverTitle,
			data: selectedOffer.data,
			editorialAngle: selectedAngle,
		})
	}, [articleTitle, discoverTitle, selectedAngle, selectedOffer])
	const imageBlockingIssues = useMemo(() => getDiscoverImageBlockingIssues(imageDimensions), [imageDimensions])
	const imageQualityIssues = useMemo(() => {
		if (!imageDimensions) return []
		const issues: string[] = []
		const aspectRatio = imageDimensions.width / imageDimensions.height
		if (Math.abs(aspectRatio - (16 / 9)) > 0.15) {
			issues.push("Billedet afviger tydeligt fra 16:9. Kontrollér beskæringen i previewet.")
		}
		return issues
	}, [imageDimensions])
	const hasConcreteImageAlt = Boolean(selectedOffer
		&& imageAltText.trim()
		&& !isGenericTravelImageAlt(imageAltText, selectedOffer.data))
	const articleSetupReady = Boolean(imageDataUrl
		&& imageDimensions
		&& !imageBlockingIssues.length
		&& hasConcreteImageAlt
		&& angleApproved
		&& selectedAngle
		&& !headlineSetupIssues.length)

	const resetArticleSetup = () => {
		setOfferApproved(false)
		setAngleSuggestions([])
		setSelectedAngle(null)
		setAngleApproved(false)
		setIsResearchingAngles(false)
		setAngleSource("")
		setAngleNotice("")
		setAngleError("")
		setImageDataUrl("")
		setImageFileName("")
		setImageDimensions(null)
		setImageAltText("")
		setArticleTitle("")
		setDiscoverTitle("")
		setIsGeneratingHeadlines(false)
		setHeadlineSource("")
		setHeadlineError("")
		setIsCreatingArticle(false)
		setArticleCreationError("")
		setCreatedArticle(null)
	}

	const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) return

		const hasTsvExtension = file.name.toLocaleLowerCase("da-DK").endsWith(".tsv")
		if (!hasTsvExtension) {
			setError("Vælg en TSV-fil med filendelsen .tsv.")
			event.target.value = ""
			return
		}

		if (file.size > 5 * 1024 * 1024) {
			setError("TSV-filen er for stor. Maksimal størrelse er 5 MB.")
			event.target.value = ""
			return
		}

		try {
			const parsed = parseTravelOfferTsv(await readFileAsText(file))
			setFileName(file.name)
			setHeaders(parsed.headers)
			setOffers(parsed.offers)
			setSelectedOffer(null)
			resetArticleSetup()
			setError("")
			setShowOfferModal(true)
		} catch (fileError) {
			setFileName("")
			setHeaders([])
			setOffers([])
			setSelectedOffer(null)
			resetArticleSetup()
			setError(fileError instanceof Error ? fileError.message : "TSV-filen kunne ikke læses.")
			event.target.value = ""
		}
	}

	const chooseOffer = (offer: ImportedTravelOffer) => {
		if (selectedOffer?.id !== offer.id) {
			resetArticleSetup()
			setSelectedOffer({
				...offer,
				data: { ...offer.data },
			})
		}
		setShowOfferModal(false)
	}

	const researchEditorialAngles = async (offerData = selectedOffer?.data) => {
		if (!offerData) return
		setIsResearchingAngles(true)
		setAngleError("")
		setAngleSuggestions([])
		setSelectedAngle(null)
		setAngleApproved(false)
		setAngleSource("")
		setAngleNotice("")
		setArticleTitle("")
		setDiscoverTitle("")
		try {
			const response = await doFetch("/eb-pristjek/articles/travel/angles", { offer: offerData })
			const data = await response.json() as TravelAnglesResponse
			if (response.status >= 400) {
				throw new Error(data.error || "Kunne ikke researche aktuelle vinkler.")
			}
			const items = Array.isArray(data.items) ? data.items.filter((item) => item?.yoastTitle && item?.whyNow) : []
			if (!items.length) throw new Error("Researchen returnerede ingen brugbare vinkler.")
			setAngleSuggestions(items)
			setAngleSource(data.source || "fallback")
			setAngleNotice(String(data.notice || ""))
		} catch (researchError) {
			setAngleError(researchError instanceof Error ? researchError.message : "Kunne ikke researche aktuelle vinkler.")
		} finally {
			setIsResearchingAngles(false)
		}
	}

	const generateHeadlineSuggestions = async (
		offerData = selectedOffer?.data,
		approvedAngle = selectedAngle,
	) => {
		if (!offerData || !approvedAngle) return
		const fallback = buildLocalHeadlineSuggestions(offerData)
		setArticleTitle(fallback.articleTitle)
		setDiscoverTitle(approvedAngle.yoastTitle)
		setIsGeneratingHeadlines(true)
		setHeadlineSource("fallback")
		setHeadlineError("")

		try {
			const response = await doFetch("/eb-pristjek/articles/travel/headlines", {
				offer: offerData,
				editorialAngle: approvedAngle,
				variationSeed: `${Date.now()}-${Math.random()}`,
			})
			const data = await response.json() as TravelHeadlineResponse
			if (response.status >= 400) {
				throw new Error(data.error || "AI-forslaget kunne ikke oprettes.")
			}
			if (!String(data.articleTitle || "").trim()) {
				throw new Error("AI returnerede ikke en artikeloverskrift.")
			}
			setArticleTitle(String(data.articleTitle || fallback.articleTitle))
			setDiscoverTitle(String(data.discoverTitle || approvedAngle.yoastTitle))
			setHeadlineSource("ai")
		} catch (headlineRequestError) {
			setHeadlineError(headlineRequestError instanceof Error
				? `${headlineRequestError.message} Standardoverskriften med den verificerede pris er indsat.`
				: "AI-forslaget kunne ikke oprettes. Standardoverskriften med den verificerede pris er indsat.")
		} finally {
			setIsGeneratingHeadlines(false)
		}
	}

	const approveOffer = () => {
		if (!selectedOffer || !canApproveOffer) return
		const verifiedOffer: ImportedTravelOffer = {
			...selectedOffer,
			data: {
				...selectedOffer.data,
				kontroltidspunkt: new Date().toISOString(),
				kontrolniveau: "Manuelt verificeret",
				ledighedsstatus: "Kontrolleret ved godkendelse",
			},
		}
		setSelectedOffer(verifiedOffer)
		setImageAltText("")
		setOfferApproved(true)
		void researchEditorialAngles(verifiedOffer.data)
	}

	const chooseEditorialAngle = (angle: TravelEditorialAngle) => {
		setSelectedAngle({
			...angle,
			facts: [...angle.facts],
			sources: angle.sources.map((source) => ({ ...source })),
		})
		setAngleApproved(false)
		setArticleTitle("")
		setDiscoverTitle(angle.yoastTitle)
		setHeadlineError("")
	}

	const approveEditorialAngle = () => {
		if (!selectedAngle || !selectedOffer || isResearchingAngles) return
		const approvedAngle = {
			...selectedAngle,
			yoastTitle: selectedAngle.yoastTitle.trim(),
			angle: selectedAngle.angle.trim(),
			whyNow: selectedAngle.whyNow.trim(),
		}
		setSelectedAngle(approvedAngle)
		setAngleApproved(true)
		void generateHeadlineSuggestions(selectedOffer.data, approvedAngle)
	}

	const handleArticleImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) {
			setImageDataUrl("")
			setImageFileName("")
			setImageDimensions(null)
			return
		}
		if (!SUPPORTED_ARTICLE_IMAGE_TYPES.has(String(file.type || "").toLocaleLowerCase("en-US"))) {
			setHeadlineError("Vælg et JPEG-, PNG-, GIF- eller WebP-billede.")
			event.target.value = ""
			return
		}
		if (file.size > 8 * 1024 * 1024) {
			setHeadlineError("Billedfilen er for stor. Maksimal størrelse er 8 MB.")
			event.target.value = ""
			return
		}

		try {
			const dataUrl = await readFileAsDataUrl(file)
			setImageDataUrl(dataUrl)
			setImageFileName(file.name)
			setImageDimensions(null)
			const image = new Image()
			image.onload = () => setImageDimensions({
				width: image.naturalWidth || image.width,
				height: image.naturalHeight || image.height,
			})
			image.onerror = () => {
				setImageDimensions(null)
				setHeadlineError("Billedet kunne ikke indlæses. Vælg en anden billedfil.")
			}
			image.src = dataUrl
			setHeadlineError("")
		} catch (imageError) {
			setImageDataUrl("")
			setImageFileName("")
			setImageDimensions(null)
			setHeadlineError(imageError instanceof Error ? imageError.message : "Billedfilen kunne ikke læses.")
		}
	}

	const createArticle = async () => {
		if (!selectedOffer || !selectedAngle || !offerApproved || !articleSetupReady || isCreatingArticle || createdArticle) return
		setIsCreatingArticle(true)
		setArticleCreationError("")

		try {
			const response = await doFetch("/eb-pristjek/articles/drafts", {
				articleType: "travel-article",
				title: articleTitle.trim(),
				discoverTitle: discoverTitle.trim(),
				editorialAngle: {
					...selectedAngle,
					yoastTitle: discoverTitle.trim(),
				},
				featuredImageUrl: imageDataUrl,
				featuredImageAlt: imageAltText.trim(),
				featuredImageWidth: imageDimensions?.width || 0,
				featuredImageHeight: imageDimensions?.height || 0,
				travelOffer: selectedOffer.data,
			})
			const data = await response.json() as TravelArticleDraftResponse
			if (response.status >= 400) {
				throw new Error(data.error || "Rejseartiklen kunne ikke oprettes.")
			}

			const articleId = Number(data.articleId || data.createdPost?.id || 0)
			if (!articleId) throw new Error("API'et returnerede ikke et article id.")
			const created = {
				articleId,
				previewLink: String(data.previewLink || `${WP_SITE}/?p=${articleId}&preview=true&no_redirect=1`),
				editLink: String(data.editLink || `${WP_SITE}/wp-admin/post.php?post=${articleId}&action=edit`),
				title: String(data.draft?.title || articleTitle.trim()),
				topic: String(data.topic || data.draft?.topic || `rejse til ${selectedOffer.data.destination}`),
				articleTypeName: String(data.articleTypeName || "Rejseartikel"),
			}
			setCreatedArticle(created)
			onArticleCreated?.(created)
		} catch (createError) {
			setArticleCreationError(createError instanceof Error ? createError.message : "Rejseartiklen kunne ikke oprettes.")
		} finally {
			setIsCreatingArticle(false)
		}
	}

	const updateField = (field: string, value: string) => {
		setSelectedOffer((current) => current
			? {
				...current,
				data: {
					...current.data,
					[field]: value,
				},
			}
			: current)
	}

	const recalculatePrices = () => {
		if (!selectedOffer) return
		const flightPrice = parsePrice(selectedOffer.data.flypris_2_voksne_dkk)
		const hotelPrice = parsePrice(selectedOffer.data.hotelpris_min_dkk)
		if (flightPrice === null || hotelPrice === null) return
		const total = Math.round(flightPrice + hotelPrice)
		setSelectedOffer((current) => current
			? {
				...current,
				data: {
					...current.data,
					samlet_pris_dkk: String(total),
					pris_pr_person_dkk: String(Math.round(total / 2)),
				},
			}
			: current)
	}

	const renderTextField = (field: string, options: { multiline?: boolean; type?: string } = {}) => {
		if (!selectedOffer) return null
		const value = selectedOffer.data[field] || ""
		const multiline = options.multiline || value.length > 120
		return (
			<Form.Group controlId={`travel-${field}`}>
				<Form.Label className="small fw-semibold mb-1">{getFieldLabel(field)}</Form.Label>
				{multiline ? (
					<Form.Control
						as="textarea"
						rows={3}
						value={value}
						onChange={(event) => updateField(field, event.target.value)}
					/>
				) : (
					<Form.Control
						type={options.type || "text"}
						min={options.type === "number" ? 0 : undefined}
						step={options.type === "number" ? 1 : undefined}
						value={value}
						onChange={(event) => updateField(field, event.target.value)}
					/>
				)}
			</Form.Group>
		)
	}

	const renderLinkField = (field: string) => {
		if (!selectedOffer) return null
		const value = selectedOffer.data[field] || ""
		const url = extractHttpUrl(value)
		return (
			<Form.Group controlId={`travel-${field}`}>
				<Form.Label className="small fw-semibold mb-1">{getFieldLabel(field)}</Form.Label>
				<div className="d-flex gap-2 align-items-start">
					<Form.Control
						className="flex-grow-1"
						type="url"
						value={value}
						placeholder="https://"
						onChange={(event) => updateField(field, event.target.value)}
					/>
					{url ? (
						<a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary text-nowrap">
							Åbn link ↗
						</a>
					) : (
						<Button variant="outline-secondary" disabled className="text-nowrap">Intet link</Button>
					)}
				</div>
			</Form.Group>
		)
	}

	return (
		<div>
			<Alert variant="info">
				<strong>Rejseartikel-workflow</strong>
				<div className="small mt-1">Upload tilbudsfilen, kontrollér ét tilbud, godkend det og klargør derefter billede og overskrifter.</div>
			</Alert>

			<Form.Group controlId="travelOffersFile" className="mb-3">
				<Form.Label>Rejsetilbud (TSV)</Form.Label>
				<Form.Control type="file" accept=".tsv,text/tab-separated-values,text/plain" onChange={handleFileChange} />
				<Form.Text className="text-muted">
					{fileName ? `${fileName} · ${offers.length} tilbud indlæst` : "Vælg en TSV-fil med samme kolonner som rejsetilbud-formatet."}
				</Form.Text>
			</Form.Group>

			{error ? <Alert variant="danger">{error}</Alert> : null}

			{offers.length ? (
				<div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
					<div className="small text-muted">{offers.length} tilbud er klar til gennemgang.</div>
					<Button variant="outline-primary" onClick={() => setShowOfferModal(true)}>
						{selectedOffer ? "Vælg et andet tilbud" : "Vælg tilbud"}
					</Button>
				</div>
			) : null}

			{selectedOffer ? (
				<div className="d-flex flex-column gap-3">
					<Card className="border-primary">
						<Card.Body>
							<div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
								<div>
									<div className="small text-primary fw-semibold mb-1">Valgt tilbud</div>
									<h5 className="mb-1">{selectedOffer.data.destination}, {selectedOffer.data.land}</h5>
									<div className="text-muted small">
										{formatTravelDate(selectedOffer.data.afrejsedato)} – {formatTravelDate(selectedOffer.data.hjemrejsedato)}
										{" · "}{selectedOffer.data.hotelnavn}
									</div>
								</div>
								<div className="text-end">
									<div className="small text-muted">Pris pr. person</div>
									<div className="fs-5 fw-bold">{formatPrice(selectedOffer.data.pris_pr_person_dkk)}</div>
								</div>
							</div>
						</Card.Body>
					</Card>

					{!offerApproved ? (
						<>
					<Card>
						<Card.Body>
							<div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
									<div>
										<h6 className="mb-1">1. Kontrollér priser</h6>
										<div className="small text-muted">Ret priserne efter kontrol. Samlet pris kan genberegnes som fly + laveste hotelpris.</div>
								</div>
								<Button
									size="sm"
									variant="outline-secondary"
									onClick={recalculatePrices}
									disabled={parsePrice(selectedOffer.data.flypris_2_voksne_dkk) === null || parsePrice(selectedOffer.data.hotelpris_min_dkk) === null}
								>
									Genberegn samlet pris
								</Button>
							</div>
							<Row className="g-3">
								{PRICE_FIELDS.map((field) => (
									<Col md={6} key={field}>{renderTextField(field, { type: "number" })}</Col>
								))}
							</Row>
						</Card.Body>
					</Card>

					<Card>
						<Card.Body>
							<h6 className="mb-1">2. Validér prislinks</h6>
							<div className="small text-muted mb-3">Links åbner i en ny fane, så de aktuelle priser kan sammenholdes med tilbuddet.</div>
							<div className="d-flex flex-column gap-3">
								{LINK_FIELDS.map((field) => <div key={field}>{renderLinkField(field)}</div>)}
							</div>
						</Card.Body>
					</Card>

					<details className="border rounded bg-white">
						<summary className="p-3" style={{ cursor: "pointer" }}>
							<span className="fw-semibold">3. Ret tilbuddets oplysninger</span>
							<span className="d-block small text-muted mt-1">Åbn for at redigere rejse-, fly- og hoteloplysninger.</span>
						</summary>
						<div className="p-3 pt-0">
							<Row className="g-3">
								{PRIMARY_DETAIL_FIELDS.map((field) => (
									<Col md={field === "artikeloverskrift_forslag" || field === "bagage_status" ? 12 : 6} key={field}>
										{renderTextField(field, {
											type: field.endsWith("dato") ? "date" : field === "antal_naetter" || field === "antal_stop" || field === "hotelstjerner" || field === "hotelrating_antal_anmeldelser" ? "number" : "text",
											multiline: field === "bagage_status",
										})}
									</Col>
								))}
							</Row>
						</div>
					</details>

					<details className="border rounded bg-white">
						<summary className="p-3 fw-semibold" style={{ cursor: "pointer" }}>
							Redigér øvrige importerede data ({additionalFields.length})
						</summary>
						<div className="p-3 pt-0">
							<Row className="g-3">
								{additionalFields.map((field) => (
									<Col md={6} key={field}>
										{renderTextField(field)}
									</Col>
								))}
							</Row>
						</div>
					</details>

					<Card className="border-success">
						<Card.Body className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
							<div>
								<h6 className="mb-1">Tilbuddet er verificeret</h6>
								<div className="small text-muted">
									Godkend tilbuddet, når priserne er rettet, og Momondo- og Trivago-linkene er kontrolleret.
								</div>
								{!canApproveOffer ? (
									<ul className="small text-danger mt-2 mb-0 ps-3">
										{approvalIssues.map((issue) => <li key={issue}>{issue}</li>)}
									</ul>
								) : null}
							</div>
							<Button variant="success" onClick={approveOffer} disabled={!canApproveOffer}>
								Godkend verificeret tilbud
							</Button>
						</Card.Body>
					</Card>
						</>
					) : (
						<>
							<Alert variant="success" className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-0">
								<div>
									<strong>Tilbuddet er godkendt</strong>
									<div className="small mt-1">Priser og links er låst som grundlag for artikelopsætningen.</div>
								</div>
								<Button
									variant="outline-success"
								size="sm"
								onClick={() => {
									resetArticleSetup()
								}}
								>
									Ret tilbuddet igen
								</Button>
							</Alert>

							<Card>
								<Card.Body>
									<div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
										<div>
											<h6 className="mb-1">4. Vælg en aktuel Discover-vinkel</h6>
											<div className="small text-muted">Vælg og godkend den redaktionelle vinkel, som skal bruges i Yoast-titlen og artikelteksten.</div>
										</div>
										<Button
											size="sm"
											variant="outline-primary"
											onClick={() => void researchEditorialAngles()}
											disabled={isResearchingAngles || isGeneratingHeadlines || isCreatingArticle || Boolean(createdArticle)}
										>
											{isResearchingAngles ? (
												<>
													<Spinner animation="border" size="sm" className="me-2" />
													Researcher...
												</>
											) : angleSuggestions.length ? "Research nye vinkler" : "Research vinkler"}
										</Button>
									</div>

									{angleError ? <Alert variant="danger" className="py-2">{angleError}</Alert> : null}
									{angleSource === "fallback" ? (
										<Alert variant="warning" className="py-2 small">
											{angleNotice || "Der blev ikke fundet en sikker, kildeunderbygget aktualitetsvinkel. Forslagene er derfor forsigtige sæson- og servicevinkler baseret på rejseperioden."}
										</Alert>
									) : angleSource === "mixed" ? (
										<Alert variant="info" className="py-2 small">{angleNotice || "Listen kombinerer researchede vinkler med sikre sæson- og servicevinkler."}</Alert>
									) : null}

									<div className="d-flex flex-column gap-2">
										{angleSuggestions.map((angle) => {
											const selected = selectedAngle?.id === angle.id
											return (
												<button
													type="button"
													key={angle.id}
													className={`text-start border rounded p-3 bg-white ${selected ? "border-primary shadow-sm" : ""}`}
													onClick={() => chooseEditorialAngle(angle)}
													disabled={isCreatingArticle || Boolean(createdArticle)}
												>
													<div className="d-flex justify-content-between align-items-start gap-2">
														<div className="fw-semibold">{angle.yoastTitle}</div>
														<Badge bg={angle.fallback ? "secondary" : "primary"}>{angle.fallback ? "Sikker fallback" : "Researchet"}</Badge>
													</div>
													<div className="small mt-2">{angle.whyNow}</div>
													<div className="small text-muted mt-2">Research: {formatResearchTime(angle.researchedAt)}</div>
												</button>
											)
										})}
									</div>

									{selectedAngle ? (
										<div className="border-top mt-3 pt-3">
											<Form.Group controlId="travelAngleYoastTitle" className="mb-3">
												<Form.Label className="fw-semibold">Yoast-/Discover-titel for vinklen</Form.Label>
												<Form.Control
													type="text"
													maxLength={80}
													value={selectedAngle.yoastTitle}
													onChange={(event) => {
														setSelectedAngle((current) => current ? { ...current, yoastTitle: event.target.value } : current)
														setDiscoverTitle(event.target.value)
														setAngleApproved(false)
													}}
													disabled={isCreatingArticle || Boolean(createdArticle)}
												/>
												<Form.Text className="text-muted">Prisfri, redaktionel og med destinationen tydeligt nævnt. {selectedAngle.yoastTitle.length}/80 tegn.</Form.Text>
											</Form.Group>
											<Form.Group controlId="travelAngleLabel" className="mb-3">
												<Form.Label className="fw-semibold">Redaktionel vinkel</Form.Label>
												<Form.Control
													type="text"
													maxLength={180}
													value={selectedAngle.angle}
													onChange={(event) => {
														setSelectedAngle((current) => current ? { ...current, angle: event.target.value } : current)
														setAngleApproved(false)
													}}
													disabled={isCreatingArticle || Boolean(createdArticle)}
												/>
											</Form.Group>
											<Form.Group controlId="travelAngleWhyNow" className="mb-3">
												<Form.Label className="fw-semibold">Hvorfor er vinklen aktuel nu?</Form.Label>
												<Form.Control
													as="textarea"
													rows={2}
													value={selectedAngle.whyNow}
													onChange={(event) => {
														setSelectedAngle((current) => current ? { ...current, whyNow: event.target.value } : current)
														setAngleApproved(false)
													}}
													disabled={isCreatingArticle || Boolean(createdArticle)}
												/>
											</Form.Group>

											<div className="small mb-2"><strong>Verificerede fakta:</strong></div>
											<ul className="small ps-3">
												{selectedAngle.facts.map((fact) => <li key={fact}>{fact}</li>)}
											</ul>
											{selectedAngle.sources.length ? (
												<div className="small mb-3">
													<strong>Kilder:</strong>{" "}
													{selectedAngle.sources.map((source, index) => (
														<span key={source.url}>
															{index ? ", " : ""}<a href={source.url} target="_blank" rel="noopener noreferrer">{source.title}</a>
														</span>
													))}
												</div>
											) : null}

											{angleSelectionIssues.length ? (
												<ul className="small text-danger ps-3">
													{angleSelectionIssues.map((issue) => <li key={issue}>{issue}</li>)}
												</ul>
											) : null}
											<div className="d-flex justify-content-end">
												<Button variant={angleApproved ? "success" : "primary"} onClick={approveEditorialAngle} disabled={!canApproveAngle || isGeneratingHeadlines || isCreatingArticle || Boolean(createdArticle)}>
													{isGeneratingHeadlines ? (
														<><Spinner animation="border" size="sm" className="me-2" />Skriver H1...</>
													) : angleApproved ? "Vinklen er godkendt" : "Godkend vinkel og skriv H1"}
												</Button>
											</div>
										</div>
									) : isResearchingAngles ? null : (
										<div className="small text-muted">Vælg en vinkel for at fortsætte.</div>
									)}
								</Card.Body>
							</Card>

							<Card>
								<Card.Body>
									<div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
										<div>
											<h6 className="mb-1">5. Klargør artikelopsætning</h6>
											<div className="small text-muted">Upload artikelbilledet, og tilpas de to overskrifter før næste trin.</div>
										</div>
										<Button
											size="sm"
											variant="outline-primary"
											onClick={() => void generateHeadlineSuggestions()}
											disabled={!angleApproved || !selectedAngle || isGeneratingHeadlines || isCreatingArticle || Boolean(createdArticle)}
										>
											{isGeneratingHeadlines ? (
												<>
													<Spinner animation="border" size="sm" className="me-2" />
													Skriver forslag...
												</>
										) : "Foreslå ny artikeloverskrift med AI"}
										</Button>
									</div>

									<Form.Group controlId="travelArticleImage" className="mb-3">
										<Form.Label className="fw-semibold">Artikelbillede</Form.Label>
										<Form.Control type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleArticleImageChange} disabled={isCreatingArticle || Boolean(createdArticle)} />
										<Form.Text className="text-muted">
											{imageFileName
												? `Valgt billede: ${imageFileName}${imageDimensions ? ` · ${imageDimensions.width} × ${imageDimensions.height} px` : ""}`
												: "Vælg et individuelt, relevant billede uden meget tekst. Det skal være mindst 1.200 px bredt og mere end 300.000 pixels, gerne omkring 16:9."}
										</Form.Text>
									</Form.Group>

									{imageDataUrl ? (
										<>
											<div className="border rounded overflow-hidden mb-2 bg-light text-center">
												<img
													src={imageDataUrl}
													alt={`Forhåndsvisning af artikelbillede til ${selectedOffer.data.destination}`}
													style={{ display: "block", width: "100%", aspectRatio: "16 / 9", objectFit: "cover" }}
												/>
											</div>
											<div className="small text-muted mb-2">16:9-preview af den beskæring, som kan blive brugt i Discover.</div>
											{imageDimensions && imageBlockingIssues.length ? (
												<Alert variant="danger" className="py-2 small">
													<strong>Billedet kan ikke bruges til denne rejseartikel.</strong>{" "}
													{imageBlockingIssues.join(" ")}
												</Alert>
											) : imageDimensions && imageQualityIssues.length ? (
												<Alert variant="info" className="py-2 small">
													<strong>Billedet er godkendt og kan bruges.</strong>{" "}
													{imageQualityIssues.join(" ")} 16:9 er kun en anbefaling og blokerer ikke oprettelsen.
												</Alert>
											) : imageDimensions ? (
												<div className="small text-success mb-3">Billedets størrelse og format er velegnet til Discover.</div>
											) : null}
										</>
									) : null}

									<Form.Group controlId="travelArticleImageAlt" className="mb-3">
										<Form.Label className="fw-semibold">Billedbeskrivelse (alt-tekst)</Form.Label>
										<Form.Control
											type="text"
											maxLength={140}
											value={imageAltText}
											onChange={(event) => setImageAltText(event.target.value)}
											disabled={isCreatingArticle || Boolean(createdArticle)}
											placeholder={`Beskriv kort, hvad billedet viser i ${selectedOffer.data.destination}`}
										/>
										<Form.Text className="text-muted">
											Beskriv det konkrete motiv, ikke blot destinationen. Feltet er obligatorisk af hensyn til tilgængelighed og billedsøgning.
										</Form.Text>
										{imageAltText.trim() && !hasConcreteImageAlt ? (
											<div className="small text-danger mt-1">Billedbeskrivelsen skal fortælle, hvad motivet viser.</div>
										) : null}
									</Form.Group>

									<Form.Group controlId="travelArticleTitle" className="mb-3">
										<div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
											<Form.Label className="fw-semibold">Overskrift i artiklen</Form.Label>
											{!isGeneratingHeadlines && headlineSource ? (
												<Badge bg={headlineSource === "ai" ? "primary" : "secondary"}>
													{headlineSource === "ai" ? "AI-forslag" : "Standardforslag"}
												</Badge>
											) : null}
										</div>
										<Form.Control
											type="text"
											maxLength={110}
											value={articleTitle}
											onChange={(event) => setArticleTitle(event.target.value)}
											disabled={isCreatingArticle || Boolean(createdArticle)}
											placeholder="Den synlige H1-overskrift i artiklen"
										/>
										<Form.Text className="text-muted">
											Denne overskrift vises som artiklens almindelige titel og skal altid indeholde den verificerede pris pr. person. {articleTitle.length}/110 tegn.
										</Form.Text>
									</Form.Group>

									<Form.Group controlId="travelDiscoverTitle">
										<Form.Label className="fw-semibold">Discover-/SEO-titel</Form.Label>
									<Form.Control
										type="text"
										maxLength={80}
										value={discoverTitle}
										onChange={(event) => {
											setDiscoverTitle(event.target.value)
											setSelectedAngle((current) => current ? { ...current, yoastTitle: event.target.value } : current)
										}}
										disabled={isCreatingArticle || Boolean(createdArticle)}
										placeholder="Den korte titel til feeds og SEO"
									/>
									<Form.Text className="text-muted">
										Dette er Yoast-titlen, som bruges som Discover-signal. Den skal være prisfri, aktuel og redaktionel. Den må gerne være tydeligt forskellig fra den tilbudsdrevne H1, men de skal handle om samme rejse. {discoverTitle.length}/80 tegn.
									</Form.Text>
									</Form.Group>

									{headlineError ? <Alert variant="warning" className="mt-3 mb-0">{headlineError}</Alert> : null}
									{(articleTitle || discoverTitle) && headlineSetupIssues.length ? (
										<Alert variant="warning" className="mt-3 mb-0 py-2">
											<ul className="mb-0 ps-3">
												{headlineSetupIssues.map((issue) => <li key={issue}>{issue}</li>)}
											</ul>
										</Alert>
									) : null}
									{imageDataUrl || articleTitle || discoverTitle ? (
										<div className="border rounded p-3 mt-3">
											<div className="fw-semibold mb-2">SEO- og Discover-tjek</div>
											<div className="d-flex gap-2 flex-wrap">
												<Badge bg={imageDataUrl && imageDimensions && !imageBlockingIssues.length && hasConcreteImageAlt ? "success" : "warning"} text={imageDataUrl && imageDimensions && !imageBlockingIssues.length && hasConcreteImageAlt ? undefined : "dark"}>Discover-billede og alt-tekst</Badge>
												<Badge bg={articleIncludesDestination ? "success" : "warning"} text={articleIncludesDestination ? undefined : "dark"}>Destination i artikeloverskrift</Badge>
												<Badge bg={articleIncludesPrice ? "success" : "warning"} text={articleIncludesPrice ? undefined : "dark"}>Pris i artikeloverskrift</Badge>
										<Badge bg={discoverIncludesDestination ? "success" : "warning"} text={discoverIncludesDestination ? undefined : "dark"}>Destination i Discover-/SEO-titel</Badge>
										<Badge bg={discoverTitle && !isOfferDrivenTravelDiscoverTitle(discoverTitle) ? "success" : "warning"} text={discoverTitle && !isOfferDrivenTravelDiscoverTitle(discoverTitle) ? undefined : "dark"}>Prisfri redaktionel Yoast-titel</Badge>
											</div>
										</div>
									) : null}
									{articleSetupReady ? (
										<Alert variant="success" className="mt-3 mb-0">
											<strong>Artikelopsætningen er klar.</strong> Billede, alt-tekst og begge overskrifter opfylder kravene.
										</Alert>
									) : (
										<div className="small text-muted mt-3">Upload et godkendt billede, skriv en konkret billedbeskrivelse, og ret de viste titelproblemer.</div>
									)}

									{articleCreationError ? <Alert variant="danger" className="mt-3 mb-0">{articleCreationError}</Alert> : null}

									{createdArticle ? (
										<Alert variant="success" className="mt-3 mb-0">
											<div className="fw-semibold mb-2">Rejseartiklen er oprettet som WordPress-kladde med ID {createdArticle.articleId}.</div>
											<div className="small mb-2">Publicér samme kalenderdag som tilbuddet blev kontrolleret. Kontrollér pris, ledighed og links igen ved senere publicering.</div>
											<div className="d-flex gap-2 flex-wrap">
												<Button as="a" href={createdArticle.previewLink} target="_blank" rel="noopener noreferrer" size="sm" variant="outline-success">
													Åbn preview
												</Button>
												<Button as="a" href={createdArticle.editLink} target="_blank" rel="noopener noreferrer" size="sm" variant="outline-dark">
													Redigér i WordPress
												</Button>
											</div>
										</Alert>
									) : (
										<div className="d-grid mt-3">
											<Button
												variant="success"
												size="lg"
												disabled={!articleSetupReady || isCreatingArticle}
												onClick={() => void createArticle()}
											>
												{isCreatingArticle ? (
													<>
														<Spinner animation="border" size="sm" className="me-2" />
														Opretter rejseartikel...
													</>
												) : "Opret rejseartikel som kladde"}
											</Button>
										</div>
									)}
								</Card.Body>
							</Card>
						</>
					)}
					</div>
			) : null}

			<Modal
				show={showOfferModal}
				onHide={() => setShowOfferModal(false)}
				size="xl"
				centered
				scrollable
				aria-labelledby="travel-offer-modal-title"
			>
				<Modal.Header closeButton>
					<Modal.Title id="travel-offer-modal-title">Vælg rejsetilbud</Modal.Title>
				</Modal.Header>
				<Modal.Body className="bg-light">
					<div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
						<div>
							<div className="fw-semibold">{fileName}</div>
							<div className="small text-muted">Sammenlign de vigtigste oplysninger og vælg det tilbud, artiklen skal tage udgangspunkt i.</div>
						</div>
						<Badge bg="dark">{offers.length} tilbud</Badge>
					</div>
					<Row xs={1} lg={2} className="g-3">
						{offers.map((offer) => (
							<Col key={offer.id}>
								<Card className={`h-100 shadow-sm ${selectedOffer?.id === offer.id ? "border-primary" : "border-0"}`}>
									<Card.Body className="d-flex flex-column">
										<OfferSummary offer={selectedOffer?.id === offer.id ? selectedOffer : offer} />
										<div className="mt-auto pt-3 d-grid">
											<Button
												variant={selectedOffer?.id === offer.id ? "primary" : "outline-primary"}
												onClick={() => chooseOffer(offer)}
											>
												{selectedOffer?.id === offer.id ? "Fortsæt med dette tilbud" : "Vælg tilbud"}
											</Button>
										</div>
									</Card.Body>
								</Card>
							</Col>
						))}
					</Row>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="outline-secondary" onClick={() => setShowOfferModal(false)}>Luk</Button>
				</Modal.Footer>
			</Modal>
		</div>
	)
}
